import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight } from "lucide-react";

const GROUPS = [
  {
    group: "General",
    items: [
      ["What is SelLend?", "SelLend is an online marketplace where people can rent, buy, and sell tools, equipment, outdoor gear, and other useful items."],
      ["Where is SelLend available?", "SelLend is launching in Ireland, with plans to expand based on demand."],
      ["Do I need an account?", "Yes. An account is required to rent, buy, sell, or list items."],
    ],
  },
  {
    group: "Renting",
    items: [
      ["How do I rent an item?", "Simply find the item you need, select your dates, submit a booking request, and wait for the owner's approval."],
      ["When do I pay?", "Payment is collected only after the owner accepts your booking request."],
      ["Can my booking request be rejected?", "Yes. Owners decide whether to accept or decline booking requests."],
      ["What happens if I don't pay after approval?", "The booking request will expire and the dates will become available again."],
    ],
  },
  {
    group: "Security Deposits",
    items: [
      ["Why do some listings require a deposit?", "A deposit helps protect owners from damage, theft, loss, or non-return."],
      ["Who sets the deposit amount?", "The owner chooses the deposit amount for each listing."],
      ["When is the deposit returned?", "Deposits are released after the item is returned and no issues are reported."],
      ["What happens if a dispute occurs?", "The deposit may be temporarily held while the dispute is reviewed."],
    ],
  },
  {
    group: "Selling",
    items: [
      ["Can I sell items instead of renting them?", "Yes. Listings may be available for rental, sale, or both."],
      ["Does SelLend take a commission?", "Yes. SelLend charges a service fee on successful transactions."],
    ],
  },
  {
    group: "Reviews",
    items: [
      ["Can I review another user?", "Yes. Reviews can be left after completed rentals and sales."],
      ["Can reviews be removed?", "Reviews may only be removed if they breach our review policies."],
    ],
  },
  {
    group: "Safety",
    items: [
      ["How does SelLend protect users?", "We use account verification, review systems, secure payment processing, and dispute resolution procedures to help create a safer marketplace."],
      ["What should I do if an item is damaged?", "Report the issue through the platform as soon as possible and provide supporting evidence where available."],
      ["What if an item is not returned?", "Owners can submit a non-return report through the platform. The case will be reviewed and appropriate action taken."],
    ],
  },
];

export default function FAQ() {
  return (
    <div data-testid="faq-page" className="min-h-screen">
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-8">
        <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-5">Help center</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]">
          Frequently asked <span className="text-[#FF5A00]">questions</span>
        </h1>
        <p className="text-lg text-muted-foreground mt-6">Everything you need to know about renting, selling, deposits, and staying safe on SelLend.</p>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 space-y-10">
        {GROUPS.map((g) => (
          <div key={g.group} data-testid={`faq-group-${g.group.toLowerCase().replace(/\s+/g, "-")}`}>
            <h2 className="font-display text-xl font-bold tracking-tight mb-3">{g.group}</h2>
            <Accordion type="single" collapsible className="bg-white border border-black/10 rounded-2xl px-5 divide-y divide-black/5">
              {g.items.map(([q, a], i) => (
                <AccordionItem key={q} value={`${g.group}-${i}`} className="border-0">
                  <AccordionTrigger data-testid={`faq-q-${g.group.toLowerCase().replace(/\s+/g, "-")}-${i}`} className="text-left font-medium hover:no-underline">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-24">
        <div className="bg-[#E3E8E1] rounded-3xl p-8 sm:p-10 text-center">
          <h2 className="font-display text-2xl font-bold tracking-tight">Still have questions?</h2>
          <p className="text-muted-foreground mt-2">Our support team is happy to help.</p>
          <Link to="/contact" data-testid="faq-cta-contact" className="inline-flex items-center gap-1.5 mt-6 bg-[#0A0A0A] text-white rounded-full px-6 py-3 font-medium hover:bg-[#FF5A00] transition-colors active:scale-95">
            Contact us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
